/**
 * Document Connections Configuration
 * 
 * Defines which columns to display for each source→target doctype combination.
 * If no config exists for a combination, falls back to generic columns.
 * 
 * NOTE: Grouping is now handled by backend via ERPNext dashboard metadata.
 * This file only contains column display configuration.
 */

export interface DocConnectionColumn {
  label: string;
  field: string; // field name from LinkedDoc
  type?: "date" | "currency" | "status" | "text";
}

interface TargetConfig {
  label?: string;
  columns: DocConnectionColumn[];
}

interface DocConnectionsConfig {
  [sourceDoctype: string]: {
    targets: {
      [targetDoctype: string]: TargetConfig;
    };
  };
}

/**
 * Configuration for document connections display.
 * 
 * Defines which columns to show for each source doctype → target doctype combination.
 * If no config exists for a combination, falls back to generic columns.
 */
export const DOC_CONNECTIONS_CONFIG: DocConnectionsConfig = {
  "Sales Order": {
    targets: {
      "Delivery Note": {
        columns: [
          { label: "Delivery Note", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Posting Date", field: "posting_date", type: "date" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
      "Sales Invoice": {
        columns: [
          { label: "Sales Invoice", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Posting Date", field: "posting_date", type: "date" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
          { label: "Outstanding", field: "outstanding_amount", type: "currency" },
        ],
      },
      "Payment Entry": {
        columns: [
          { label: "Payment Entry", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Posting Date", field: "posting_date", type: "date" },
          { label: "Paid Amount", field: "paid_amount", type: "currency" },
        ],
      },
    },
  },
  
  "Item": {
    targets: {
      "Sales Order": {
        columns: [
          { label: "Sales Order", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "posting_date", type: "date" },
          { label: "Customer", field: "customer", type: "text" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
      "Purchase Order": {
        columns: [
          { label: "Purchase Order", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "posting_date", type: "date" },
          { label: "Supplier", field: "supplier", type: "text" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
      "Sales Invoice": {
        columns: [
          { label: "Sales Invoice", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "posting_date", type: "date" },
          { label: "Customer", field: "customer", type: "text" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
      "Purchase Invoice": {
        columns: [
          { label: "Purchase Invoice", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "posting_date", type: "date" },
          { label: "Supplier", field: "supplier", type: "text" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
      "Delivery Note": {
        columns: [
          { label: "Delivery Note", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "posting_date", type: "date" },
          { label: "Customer", field: "customer", type: "text" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
      "Purchase Receipt": {
        columns: [
          { label: "Purchase Receipt", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "posting_date", type: "date" },
          { label: "Supplier", field: "supplier", type: "text" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
      "Stock Entry": {
        columns: [
          { label: "Stock Entry", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "posting_date", type: "date" },
          { label: "Purpose", field: "purpose", type: "text" },
        ],
      },
      "Material Request": {
        columns: [
          { label: "Material Request", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "transaction_date", type: "date" },
          { label: "Material Request Type", field: "material_request_type", type: "text" },
        ],
      },
      "Quotation": {
        columns: [
          { label: "Quotation", field: "name", type: "text" },
          { label: "Status", field: "status", type: "status" },
          { label: "Date", field: "transaction_date", type: "date" },
          { label: "Customer", field: "customer", type: "text" },
          { label: "Grand Total", field: "grand_total", type: "currency" },
        ],
      },
    },
  },
  
  // Add more source doctypes as needed
};

/**
 * Get column configuration for a source→target doctype combination.
 * 
 * @param sourceDoctype - Source document type
 * @param targetDoctype - Target document type
 * @returns Column configuration or null if not found
 */
export function getConnectionColumns(
  sourceDoctype: string,
  targetDoctype: string
): DocConnectionColumn[] | null {
  const sourceConfig = DOC_CONNECTIONS_CONFIG[sourceDoctype];
  if (!sourceConfig) return null;
  
  const targetConfig = sourceConfig.targets[targetDoctype];
  if (!targetConfig) return null;
  
  return targetConfig.columns;
}

/**
 * Get default/generic columns for any doctype.
 * Used as fallback when no specific config exists.
 */
export function getDefaultColumns(): DocConnectionColumn[] {
  return [
    { label: "Name", field: "name", type: "text" },
    { label: "Status", field: "status", type: "status" },
    { label: "Date", field: "posting_date", type: "date" },
  ];
}
